import { graphql } from 'react-relay';
import { useState, useEffect } from 'react';

import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers.ts';
import { getSpaceLink } from '@confluence/route-manager-utils/entry-points/url.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { PAGE_SIZE } from './RecommendationTile.tsx';
import type { useFeedRecommendationsFeedRecommendationQuery as useFeedRecommendationsFeedRecommendationQuery$data } from './__types__/useFeedRecommendationsFeedRecommendationQuery.ts';
const useFeedRecommendationsFeedRecommendationQuery = graphql`
	query useFeedRecommendationsFeedRecommendationQuery {
		getRecommendedFeedUserConfig {
			recommendedSpaces {
				score
				space {
					id
					key
					name
					icon {
						path
					}
				}
			}
			recommendedPeople {
				score
				user {
					id
					title
					displayName
					isActive
					photos {
						value
						isPrimary
					}
				}
			}
		}
		userPreferences {
			feedRecommendedUserSettingsDismissTimestamp
		}
	}
`;
export type RecommendationType = {
	score: number;
	name: string;
	icon: string;
	id: string;
	url: string;
	type: 'space' | 'user';
	title?: string;
};

export const useFeedRecommendations = () => {
	const {
		data,
		loading: recsLoading,
		error: feedRecError,
	} = useQueryInterop<useFeedRecommendationsFeedRecommendationQuery$data>(
		useFeedRecommendationsFeedRecommendationQuery,
		{
			errorPolicy: 'all',
			fetchPolicy: 'cache-and-network',
		},
	);

	const [recs, setRecs] = useState<RecommendationType[]>([]);
	const [initialRecSize, setInitialRec] = useState<number>(0);
	const recsDismissed = data?.userPreferences?.feedRecommendedUserSettingsDismissTimestamp;
	const userRecs = data?.getRecommendedFeedUserConfig?.recommendedPeople;
	const spaceRecs = data?.getRecommendedFeedUserConfig?.recommendedSpaces;

	const msInMonth = 2629800000;
	const showRecs =
		recsDismissed && recsDismissed !== 'NO_VALUE_SET'
			? Math.floor((new Date().getTime() - new Date(recsDismissed).getTime()) / msInMonth) >= 1
			: recsDismissed === 'NO_VALUE_SET';

	const initialState = showRecs && initialRecSize >= PAGE_SIZE && recs.length > 0;

	const [showFeedRecs, setShowFeedRecs] = useState(initialState);

	if (initialState != showFeedRecs) {
		setShowFeedRecs(initialState);
	}

	useEffect(() => {
		const userRecsArray: RecommendationType[] = [];
		const spaceRecsArray: RecommendationType[] = [];

		if (userRecs) {
			userRecs.forEach((user) => {
				if (user.user && user.user.displayName && user.user.id && user.user.isActive) {
					userRecsArray.push({
						score: user.score,
						name: user.user.displayName,
						icon: user.user.photos?.find((photo) => photo?.isPrimary)?.value || '',
						id: user.user.id,
						url: getAtlassianPeopleProfileUrl(user.user.id, 'feed-alpha', CONTEXT_PATH),
						title: user.user.title || '',
						type: 'user',
					});
				}
			});
		}

		if (spaceRecs) {
			spaceRecs.forEach((space) => {
				if (space.space && space.space.name && space.space.id && space.space.key)
					spaceRecsArray.push({
						score: space.score,
						name: space.space.name,
						icon: space.space.icon?.path || '',
						id: space.space.id,
						url: getSpaceLink(space.space.key),
						type: 'space',
					});
			});
		}

		userRecsArray.sort((a, b) => b.score - a.score);
		spaceRecsArray.sort((a, b) => b.score - a.score);
		const recsArray = userRecsArray.concat(spaceRecsArray);
		setRecs(recsArray);
		setInitialRec(recsArray.length);
	}, [userRecs, spaceRecs, setRecs, setInitialRec]);

	return {
		showFeedRecs,
		setShowFeedRecs,
		recs,
		setRecs,
		recsLoading,
		feedRecError,
	};
};
